import React, { useState } from "react"
import { useSelector } from "react-redux"
import {
  Box,
  IconButton,
  Typography,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import MoreVertIcon from "@mui/icons-material/MoreVert"

import {
  getOrganizationsEnterpriseSelector,
  getCatalogProductTypesSelector,
} from "../../../../reducers/Organizations/organizations"
import PackagingSettingsModal from "./PackagingSettingsModal"
import PackagingSettingsEditModal from "./PackagingSettingsEditModal"
import CatalogSourceModal from "../cardSettings/CatalogSourceModal"

const PackagingSettingsSection = () => {
  const enterprise = useSelector(getOrganizationsEnterpriseSelector)
  const catalogProductTypes = useSelector(getCatalogProductTypesSelector)
  const packagings = useSelector((state) => state.packagingList.packagingList)

  const [addModalOpen, setAddModalOpen] = useState(false)
  const [editSetting, setEditSetting] = useState(null)
  const [catalogModalOpen, setCatalogModalOpen] = useState(false)
  const settings = enterprise?.packagingSettings || []

  const getLabel = (kfcName) => {
    const pt = catalogProductTypes.find((p) => p.kfcName === kfcName)
    return pt ? pt.label : kfcName
  }

  const getPackagingName = (packagingId) => {
    if (!packagingId) return "—"
    const p = packagings.find((pkg) => pkg.id === packagingId)
    return p ? p.get("name") : "—"
  }

  return (
    <Box sx={sx.card}>
      {/* card-head */}
      <Box sx={sx.cardHead}>
        <Typography sx={sx.cardTitle}>Conditionnement</Typography>
        <Box
          component="button"
          onClick={() => setAddModalOpen(true)}
          sx={sx.addBtn}
        >
          <AddIcon sx={{ fontSize: 16 }} />
          Ajouter un packaging
        </Box>
      </Box>

      {/* pc-cards */}
      {settings.length === 0 ? (
        <Box
          sx={sx.dashedBtn}
          onClick={() => setAddModalOpen(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && setAddModalOpen(true)}
        >
          <Box sx={sx.dashedIcon} className="dashed-icon">
            <AddIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography sx={sx.dashedTitle}>Paramétrer le conditionnement</Typography>
          <Typography sx={sx.dashedSub}>Définissez le packaging, le sous-packaging et la DLC par catégorie de recettes</Typography>
        </Box>
      ) : (
        <Box sx={sx.pcCards}>
          {settings.map((s) => (
            <Box key={s.productType} sx={sx.pcCard}>
              {/* pc-card-head */}
              <Box sx={sx.pcCardHead}>
                <Typography sx={sx.pcCardTitle}>{getLabel(s.productType)}</Typography>
                <IconButton size="small" onClick={() => setEditSetting(s)} title="Modifier" aria-label="Modifier" sx={sx.menuBtn}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* pc-card-body */}
              <Box sx={sx.pcCardBody}>
                <Box sx={sx.pcField}>
                  <Typography sx={sx.pcFieldLabel}>Packaging</Typography>
                  <Typography sx={sx.pcFieldValue}>{getPackagingName(s.packagingId)}</Typography>
                </Box>
                <Box sx={sx.pcField}>
                  <Typography sx={sx.pcFieldLabel}>Sous-packaging</Typography>
                  <Typography sx={sx.pcFieldValue}>{getPackagingName(s.subPackagingId)}</Typography>
                </Box>
                <Box sx={sx.pcField}>
                  <Typography sx={sx.pcFieldLabel}>DLC</Typography>
                  <Typography sx={sx.pcFieldValue}>
                    {s.dlc ? `${s.dlc} ${s.dlc > 1 ? "jours" : "jour"}` : "—"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {addModalOpen && (
        <PackagingSettingsModal
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onSave={() => setAddModalOpen(false)}
          onGoToCatalog={() => { setAddModalOpen(false); setCatalogModalOpen(true) }}
        />
      )}

      {!!editSetting && (
        <PackagingSettingsEditModal
          open={!!editSetting}
          setting={editSetting}
          onClose={() => setEditSetting(null)}
          onSave={() => setEditSetting(null)}
        />
      )}

      {catalogModalOpen && (
        <CatalogSourceModal
          open={catalogModalOpen}
          onClose={() => setCatalogModalOpen(false)}
          enterpriseId={enterprise?.objectId}
          existingSources={enterprise?.catalogSources || []}
        />
      )}
    </Box>
  )
}

const sx = {
  card: {
    bgcolor: "#FFFFFF",
    borderRadius: "4px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.06), 0 1px 1px rgba(0,0,0,0.04)",
    p: "24px",
  },
  cardHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mb: "16px",
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 500,
    color: "#262626",
  },
  addBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "transparent",
    border: "1px solid #2196F3",
    color: "#2196F3",
    fontFamily: "inherit",
    fontSize: 14,
    fontWeight: 500,
    px: "12px",
    py: "6px",
    borderRadius: "4px",
    cursor: "pointer",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    transition: "background 150ms",
    "&:hover": { bgcolor: "#E3F2FD" },
  },
  pcCards: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  pcCard: {
    border: "1px solid #E6E6E6",
    borderRadius: "6px",
    bgcolor: "#fff",
    p: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  pcCardHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
  },
  pcCardTitle: {
    fontSize: 16,
    fontWeight: 500,
    color: "#262626",
  },
  menuBtn: {
    color: "#555555",
  },
  pcCardBody: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "12px 24px",
  },
  pcField: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    minWidth: 0,
  },
  pcFieldLabel: {
    fontSize: 12,
    color: "#7C7C7C",
    fontWeight: 400,
  },
  pcFieldValue: {
    fontSize: 14,
    color: "#262626",
  },
  dashedBtn: {
    border: "1.5px dashed #CCCCCC",
    borderRadius: "8px",
    py: "32px",
    px: "20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: "8px",
    bgcolor: "#fcfcfc",
    cursor: "pointer",
    transition: "all 150ms",
    "&:hover": {
      borderColor: "primary.main",
      bgcolor: "#E8F1FE",
      "& .dashed-icon": {
        borderColor: "primary.main",
        color: "primary.main",
      },
    },
  },
  dashedIcon: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    bgcolor: "white",
    border: "1.5px solid #CCCCCC",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#7C7C7C",
    transition: "all 150ms",
  },
  dashedTitle: {
    fontSize: 15,
    fontWeight: 500,
    color: "#262626",
    mt: "4px",
  },
  dashedSub: {
    fontSize: 13,
    color: "#7C7C7C",
    maxWidth: 380,
    lineHeight: 1.45,
  },
}

export default PackagingSettingsSection
